import React, { useEffect, useState } from 'react';
import { PayoutMethodTypes, deletePayoutMethod } from 'api/payoutMethods';
import { Typography } from 'ui-kit';
import { message } from 'antd';
import { PayoutMethod } from 'api/types/payout';
import ConfirmModal from 'components/ConfirmModal';
import { YOU_DO_NOT_HAVE_PERMISSION_TO_THIS_ACTION_ERROR } from 'consts';

type Props = {
  allPayoutMethods: PayoutMethod[];
  payoutMethod: PayoutMethod;
  visible: boolean;
  handleClose: () => void;
  invalidate: () => void;
};

export default function DeletePayoutMethodModal({
  payoutMethod,
  allPayoutMethods,
  visible,
  handleClose,
  invalidate,
}: Props) {
  const [nextPayoutMethod, setNextPayoutMethod] = useState<PayoutMethod | null>(null);
  const onSubmit = () => {
    deletePayoutMethod(payoutMethod.id).then((res) => {
      if (res.ok) {
        message.info('Payout method deleted successfully!');
        invalidate();
        handleClose();
      } else {
        // Error if the user is cohost
        if (
          Object.values(res?.errors?.fields)?.[0] ===
          YOU_DO_NOT_HAVE_PERMISSION_TO_THIS_ACTION_ERROR
        ) {
          message.error(YOU_DO_NOT_HAVE_PERMISSION_TO_THIS_ACTION_ERROR);
          return;
        }

        message.error(res.data || 'Error deleting payout method.');
      }
    });
  };

  useEffect(() => {
    if (payoutMethod === null || allPayoutMethods === null) {
      return;
    }
    const getDefaultPayoutMethod = () => {
      const otherPayoutMethods = allPayoutMethods?.filter((pm) => pm.id !== payoutMethod?.id);
      if (otherPayoutMethods?.length === 0) {
        return null;
      }
      return otherPayoutMethods.sort((a, b) => {
        if (a.isDefault || b.isDefault) {
          return +a.isDefault - +b.isDefault;
        }
        if (a.type !== b.type) {
          return a.type === PayoutMethodTypes.STRIPE ? 1 : -1;
        }
        return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
      })[0];
    };
    setNextPayoutMethod(getDefaultPayoutMethod());
  }, [allPayoutMethods, payoutMethod]);
  return (
    <ConfirmModal
      visible={visible}
      onCancel={handleClose}
      confirmText="Delete"
      cancelText="Cancel"
      onConfirm={onSubmit}
      destroyOnClose
    >
      <Typography variant="body1">
        Are you sure you want to delete your payout method &quot;{payoutMethod?.nickname}&quot;?
      </Typography>
      <Typography variant="body2">
        {nextPayoutMethod === null ? (
          <span>You will need to add a new payout method before you can receive payouts.</span>
        ) : (
          <span>
            Any stores associated with this payout method will be updated to use the payout method
            &quot;{nextPayoutMethod.nickname}&quot;
          </span>
        )}
      </Typography>
    </ConfirmModal>
  );
}
